/**
 * Global error boundary — catches render errors anywhere below it and shows
 * a useful fallback UI with the error message + a Reset button, instead of
 * leaving the dashboard blank.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] uncaught render error:', error, info)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-background p-6">
        <Card className="w-[min(92vw,640px)] border-destructive/30">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <CardTitle>Đã xảy ra lỗi giao diện</CardTitle>
                <CardDescription>Một thành phần đã gặp lỗi khi render. Bạn có thể thử lại.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <pre className="max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs text-foreground">
              {error.name}: {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </pre>
            <div className="flex gap-2">
              <Button onClick={this.reset}>Thử lại</Button>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Tải lại trang
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }
}
